import "./App.css";
import { useEffect, useState, useRef, Suspense } from "react";
import Navbar from "./Components/js/Navbar/Navbar";
import AppSibebar from "./Components/js/Sidebar/AppSidebar";
import Login from "./Components/js/Login/Login";
import { routesMap } from "./Routes";
import { BrowserRouter, Routes, Route, Outlet, useNavigate } from "react-router-dom";
import { Provider } from "react-redux";
import store from "./redux/store";
import AlertModal from "./Components/js/Modal/AlertModal";
import { oktaConfig } from "./Components/js/OktaLogin/OktaConfig";
import {
  Security,
  LoginCallback,
  // useOktaAuth as okatauth_t,
  useOktaAuth,
} from "@okta/okta-react";
import { OktaAuth } from "@okta/okta-auth-js";
import "bootstrap/dist/css/bootstrap.min.css";
import { Footer } from "./Components/js/Footer/Footer";
import { Subheader } from "./Components/js/Subheader/Subheader";
import { AuthGate } from "./AuthGate";
import {
  apiuserLogout,
  getApiCall,
  userLogout,
} from "./Components/js/Utils/Utils";
import { AuthManager } from "./AuthManager";
import Page404 from "./Components/js/404/404";
const oktaAuth = new OktaAuth(oktaConfig);

const restoreOriginalUri = async (_oktaAuth, originalUri) => {
  window.location.href = originalUri || "/";
};


function AppLayout() {
  const headerRef = useRef(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const update = () => {
      document.documentElement.style.setProperty(
        "--navbar-height",
        `${el.getBoundingClientRect().height}px`
      );
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <header className="App-header" ref={headerRef}>
        <Navbar />
      </header>
      <div className="layout-with-sidebar">
        <AppSibebar />
        <div className="content-column">
          <div className="main-content">
            <Suspense fallback={<div>Loading...</div>}>
              <Outlet />
            </Suspense>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
}

function AppContent() {
  const { authState, oktaAuth: oktaAuthInstance } = useOktaAuth();
  const [isTokenValid, setIsTokenValid] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkToken = async () => {

      // If authState is still loading, wait
      if (!authState) {
        // console.log("AuthState is still loading...");
        return;
      }

      // If we're on the callback route, don't do token validation yet
      if (window.location.pathname === "/login/callback") {
        // console.log("On callback route, skipping token validation");
        setIsLoading(false);
        return;
      }

      // If not authenticated, don't check token
      if (!authState.isAuthenticated) {
        // console.log("User not authenticated");
        setIsTokenValid(false);
        setIsLoading(false);
        return;
      }

      try {
        // console.log("User authenticated, checking token...");
        // console.log(
        //   "Access token present:",
        //   !!authState.accessToken?.accessToken
        // );

        const tokenUrl = "/api/token-check/";
        const response = await getApiCall(oktaAuthInstance, tokenUrl, {});
        // console.log("Token check response status:", response.status);
        setIsTokenValid(true);
      } catch (error) {
        console.error("Error during token check:", error);
        setIsTokenValid(false);
        // For now, let's not sign out on API errors to debug
        await oktaAuthInstance.signOut();
      } finally {
        setIsLoading(false);
      }
    };

    checkToken();
  }, [authState, oktaAuthInstance]);

  return (
    <div className="App">
      <Provider store={store}>
        <BrowserRouter>
          <AuthManager />
          <Routes>
            {/* Always handle callback route first, regardless of auth state */}
            <Route path="/login/callback" element={<LoginCallback />} />

            {/* Handle other routes based on auth state */}
            <Route
              path="/*"
              element={
                <MainAppRoutes
                  authState={authState}
                  isLoading={isLoading}
                  isTokenValid={isTokenValid}
                  oktaAuthInstance={oktaAuthInstance}
                />
              }
            />
          </Routes>
        </BrowserRouter>
      </Provider>
    </div>
  );
}

function MainAppRoutes({
  authState,
  isLoading,
  isTokenValid,
  oktaAuthInstance,
}) {
  // Show loading while checking authentication state
  if (isLoading || !authState) {
    return <div>Loading authentication...</div>;
  }

  // If not authenticated, show redirect message
  if (!authState.isAuthenticated) {
    return (
      <div>
        <div>Redirecting to login...</div>
        <div style={{ marginTop: "10px", fontSize: "12px", color: "#666" }}>
          If you're not redirected automatically,{" "}
          <button onClick={() => oktaAuthInstance.signInWithRedirect()}>
            click here
          </button>
        </div>
      </div>
    );
  }

  // If authenticated but token is invalid
  if (isTokenValid === false) {
    return <div>Authentication failed. Please try again.</div>;
  }

  // If still checking token validity
  if (isTokenValid === null) {
    return <div>Validating session...</div>;
  }
  // Render the main app
  return (
    <Routes>
      <Route element={<AppLayout />}>
        {routesMap?.map((route, index) => (
          <Route
            key={index}
            path={route?.path || "/"}
            element={
              route?.permission ? (
                <div>Protected Route - Permission Required</div>
              ) : (
                <>
                  <AlertModal />
                  {["analytics", "activation", "data-manager"].some((k) =>
                    route?.path.includes(k)
                  ) && <Subheader />}
                  <route.component />
                </>
              )
            }
          />
        ))}
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="*" element={<Page404 />} />
    </Routes>
  );
}

function App() {
  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <AppContent />
    </Security>
  );
}

export default App;
